/**
 * CalculatorLeadCapture — Auto-trigger lead capture on ALL calculator pages.
 *
 * This component is placed once in App.js and automatically detects when the
 * user is on a /resources/calculators/* page. After 30 seconds on the page
 * (or scrolling past 50%), it shows the LeadCaptureModal.
 *
 * The calculator type is derived from the URL slug, so no changes are needed
 * to individual calculator components.
 *
 * Skips if:
 * - User is on the calculator hub/category pages (not a specific calculator)
 * - LeadCaptureModal was already shown for this calculator in this session
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import LeadCaptureModal from './LeadCaptureModal';

// No exclusions — all calculator pages get the 30s auto-popup.
// Individual calculators may still have their own button-triggered modal;
// sessionStorage prevents showing the auto-popup twice.
const CALCULATORS_WITH_INLINE_MODAL = [];

// Map URL slugs to calculator types for the modal
const SLUG_TO_TYPE = {
  'sip': 'sip',
  'sip-delay': 'sip',
  'sip-growth': 'sip',
  'elss': 'tax',
  'capital-gains-tax': 'tax',
  'hra-exemption': 'tax',
  'gst': 'tax',
  'freelancer-income-tax': 'tax',
  'retirement-plan': 'retirement',
  'superannuation': 'retirement',
  'fire': 'retirement',
  'pension': 'retirement',
  'postretirement': 'retirement',
  'nps': 'retirement',
  'nps-reinvestment': 'retirement',
  'reverse-mortgage': 'retirement',
  'gratuity': 'retirement',
};

const TRIGGER_DELAY_MS = 30000; // Show after 30 seconds on a calculator page
const SCROLL_THRESHOLD = 0.5;   // Or after scrolling past 50% of the page

const CalculatorLeadCapture = () => {
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Extract calculator slug from URL
  const getCalculatorSlug = useCallback(() => {
    const match = location.pathname.match(/^\/resources\/calculators\/([a-z0-9-]+)$/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const slug = getCalculatorSlug();

  // Check if this is a valid calculator page (not hub/category)
  const isCalculatorPage = slug && !['investment-calculators', 'personal-finance-calculators',
    'family-calculators', 'retirement-calculators', 'education-career-calculators',
    'goal-planning-calculators', 'tax-calculators', 'income-cashflow-calculators',
    'trading-calculators', 'loan-calculators', 'miscellaneous-calculators'].includes(slug);

  // Skip if calculator already has its own inline modal
  const hasInlineModal = slug && CALCULATORS_WITH_INLINE_MODAL.includes(slug);

  // Get calculator type for the modal
  const calculatorType = slug ? (SLUG_TO_TYPE[slug] || 'default') : 'default';

  // Session storage key to avoid repeat prompts
  const sessionKey = `calcLeadShown_${slug}`;

  useEffect(() => {
    // Reset state on navigation
    setShowModal(false);
    setDismissed(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isCalculatorPage || hasInlineModal || dismissed) return;

    // Check if already shown this session
    try {
      if (sessionStorage.getItem(sessionKey)) return;
    } catch (e) {
      // sessionStorage unavailable
    }

    let timeoutId;
    let scrollHandler;

    // Timer trigger
    timeoutId = setTimeout(() => {
      setShowModal(true);
      try { sessionStorage.setItem(sessionKey, 'true'); } catch (e) { /* ignore */ }
    }, TRIGGER_DELAY_MS);

    // Scroll trigger
    scrollHandler = () => {
      const scrolled = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrolled > SCROLL_THRESHOLD) {
        setShowModal(true);
        try { sessionStorage.setItem(sessionKey, 'true'); } catch (e) { /* ignore */ }
        window.removeEventListener('scroll', scrollHandler);
        clearTimeout(timeoutId);
      }
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [isCalculatorPage, hasInlineModal, dismissed, sessionKey]);

  if (!isCalculatorPage || hasInlineModal) return null;

  return (
    <LeadCaptureModal
      isOpen={showModal}
      onClose={() => {
        setShowModal(false);
        setDismissed(true);
      }}
      calculatorType={calculatorType}
      calculatorResults={{
        page_url: location.pathname,
        trigger: 'auto_calculator_lead_capture',
      }}
    />
  );
};

export default CalculatorLeadCapture;
